/** Set-piece placement and taker selection. Source: teamAIcontroller.cpp:670-922. */

import { Vector3 } from 'three';
import { BALL, PITCH } from '../core/constants';
import { clamp, curve, normalizedClamp, normalizedOr } from '../core/math';
import { keeperKickOffPosition, placeKickOff, type FormationEntry } from './formation';
import type { Match, MatchPlayer, TeamState } from './match';

export enum SetPiece {
  None = 'none',
  KickOff = 'kickoff',
  GoalKick = 'goalkick',
  FreeKick = 'freekick',
  Corner = 'corner',
  ThrowIn = 'throwin',
  Penalty = 'penalty',
}

export interface SetPieceSetup {
  type: SetPiece;
  teamId: number;
  position: Vector3;
}

interface Placement {
  backX: number;
  frontX: number;
  lowY: number;
  highY: number;
  xFocus?: number;
  xFocusStrength?: number;
  yFocus?: number;
  yFocusStrength?: number;
  microFocus?: Vector3;
  microFocusStrength?: number;
  midfieldFocus?: number;
  midfieldFocusStrength?: number;
}

/** Reset the ball and both teams, returning the player who takes the restart. */
export function prepareSetPiece(match: Match, setup: SetPieceSetup): MatchPlayer {
  const ballPosition = setup.position.clone().setZ(BALL.radius);
  match.ball.setPosition(ballPosition);

  const positions = new Map<MatchPlayer, Vector3>();
  for (const team of match.teams) {
    if (setup.type === SetPiece.KickOff) {
      const placed = placeKickOff(
        team.players.map((player) => player.formationEntry),
        team.side,
        { isTaker: team.id === setup.teamId, jitter: () => 0.5 },
      );
      team.players.forEach((player, index) => positions.set(player, placed[index]!.position));
      continue;
    }

    const placement = restartPlacement(setup, team);
    for (const player of team.players) {
      const position =
        player.role === 'GK'
          ? keeperKickOffPosition(team.side)
          : mapFormation(player.formationEntry, team.side, placement);
      positions.set(player, position);
    }
  }

  const takingTeam = match.team(setup.teamId);
  const taker = takingTeam.players.reduce((closest, player) =>
    positions.get(player)!.distanceToSquared(setup.position) <
    positions.get(closest)!.distanceToSquared(setup.position)
      ? player
      : closest,
  );

  const radial = normalizedOr(
    setup.position.clone(),
    new Vector3(0, -takingTeam.side, 0),
  );
  const takerDistance =
    setup.type === SetPiece.ThrowIn || setup.type === SetPiece.KickOff
      ? 0.3
      : setup.type === SetPiece.Penalty
        ? 3
        : 2.3;
  const takerDirection =
    setup.type === SetPiece.FreeKick || setup.type === SetPiece.Penalty
      ? new Vector3(takingTeam.side, 0, 0)
      : radial;
  positions.set(taker, setup.position.clone().addScaledVector(takerDirection, takerDistance));

  if (setup.type === SetPiece.FreeKick) {
    const defendingTeam = match.opponentTeam(setup.teamId);
    for (const player of defendingTeam.players) {
      const position = positions.get(player)!;
      const relative = position.clone().sub(setup.position);
      if (relative.length() < 9.15) {
        position.copy(setup.position).addScaledVector(
          normalizedOr(relative, new Vector3(defendingTeam.side, 0, 0)),
          9.15,
        );
      }
    }

    const goal = new Vector3(PITCH.halfLength * defendingTeam.side, 0, 0);
    if (goal.distanceTo(setup.position) < 40) {
      const wall = [...defendingTeam.players]
        .sort(
          (a, b) =>
            positions.get(a)!.distanceToSquared(setup.position) -
            positions.get(b)!.distanceToSquared(setup.position),
        )
        .slice(0, 3);
      wall.forEach((player, index) => {
        const toGoal = goal
          .clone()
          .sub(setup.position)
          .normalize()
          .add(new Vector3(0, 1 - index, 0).multiplyScalar(0.07))
          .normalize();
        positions.get(player)!.copy(setup.position).addScaledVector(toGoal, 9.15);
      });
    }
  }

  if (setup.type === SetPiece.Penalty) {
    const penaltySide = setup.position.x < 0 ? -1 : 1;
    for (const team of match.teams) {
      for (const player of team.players) {
        if (player.role === 'GK') continue;
        const position = positions.get(player)!;
        if (position.x * penaltySide > PITCH.halfLength - 17) {
          position.x = (PITCH.halfLength - 17) * penaltySide;
        }
        const relative = position.clone().sub(setup.position);
        if (relative.length() < 9.65) {
          position.copy(setup.position).addScaledVector(
            normalizedOr(relative, new Vector3(-penaltySide, 0, 0)),
            9.65,
          );
        }
      }
    }
    positions.set(taker, setup.position.clone().addScaledVector(takerDirection, takerDistance));
  }

  for (const team of match.teams) {
    const minimumDistance = team.id === setup.teamId ? 2 : 5;
    for (const player of team.players) {
      if (player === taker) continue;
      const position = positions.get(player)!;
      const relative = position.clone().sub(setup.position);
      if (relative.length() < minimumDistance) {
        position.copy(setup.position).addScaledVector(
          normalizedOr(relative, new Vector3(team.side, 0, 0)),
          minimumDistance,
        );
      }
    }
  }

  for (const [player, position] of positions) {
    if (player.anim.resetPosition) player.anim.resetPosition(position, setup.position);
    else player.position.copy(position);
  }
  if (setup.type === SetPiece.ThrowIn) taker.anim.selectRetainAnim?.();
  // Everybody has just been teleported into position, so the speed history is meaningless
  // and would otherwise read as a whole squad sprinting. The original clears it here too.
  // The same teleport must not be charged to anyone's legs: walking back for a corner is not
  // fifty metres of running.
  for (const player of match.players) {
    player.clearPositionHistory();
    player.forgetPreviousPosition();
  }
  // Whatever happened before the restart no longer counts. In particular the keeper is free to
  // handle the ball again: a back-pass from before a throw-in is not still a back-pass after it.
  match.clearTouchRecord();
  match.resetSerial++;
  return taker;
}

function restartPlacement(setup: SetPieceSetup, team: TeamState): Placement {
  const taking = team.id === setup.teamId;
  const side = team.side;
  const x = setup.position.x;
  const y = setup.position.y;

  if (setup.type === SetPiece.GoalKick) {
    return {
      backX: PITCH.halfLength * side * (taking ? 0.5 : 0.4),
      frontX: PITCH.halfLength * side * (taking ? -0.2 : -0.1),
      lowY: -PITCH.halfWidth * 0.7,
      highY: PITCH.halfWidth * 0.7,
    };
  }

  if (setup.type === SetPiece.Corner) {
    const backX = PITCH.halfLength * side * (taking ? -0.2 : 0.98);
    const frontX = PITCH.halfLength * side * (taking ? -0.96 : 0.5);
    return {
      backX,
      frontX,
      lowY: -PITCH.halfWidth * 0.6,
      highY: PITCH.halfWidth * 0.6,
      xFocus: taking ? frontX * 0.85 : backX * 0.94,
      xFocusStrength: taking ? 0.7 : 0.8,
      yFocus: y * 0.1,
      yFocusStrength: taking ? 0.7 : 0.9,
      microFocus: new Vector3(x * 0.95, y * 0.1, 0),
      microFocusStrength: 0.9,
      midfieldFocus: taking ? 0.9 : 0.1,
      midfieldFocusStrength: taking ? 0.5 : 0.7,
    };
  }

  if (setup.type === SetPiece.FreeKick) {
    const xOffset = clamp((x * -side) / PITCH.halfLength, -1, 1) * 0.5 + 0.5;
    const backX = PITCH.halfLength * side * (taking ? 0.7 - xOffset * 0.7 : 1 - xOffset * 0.6);
    const frontX = PITCH.halfLength * side * (taking ? -0.3 - xOffset * 0.7 : 0.5 - xOffset * 0.8);
    return {
      backX: clamp(backX, -PITCH.halfLength, PITCH.halfLength),
      frontX: clamp(frontX, -PITCH.halfLength, PITCH.halfLength),
      lowY: -PITCH.halfWidth * 0.7,
      highY: PITCH.halfWidth * 0.7,
      xFocus: clamp(x + (taking ? -10 : 20) * side, -PITCH.halfLength, PITCH.halfLength),
      xFocusStrength: taking ? 0.5 + xOffset * 0.2 : 0.6 - xOffset * 0.4,
      yFocus: y * (taking ? 0.4 : 0.2),
      yFocusStrength: taking ? 0.6 + xOffset * 0.2 : 0.8 - xOffset * 0.4,
      microFocus: setup.position,
      microFocusStrength: 0.4,
    };
  }

  if (setup.type === SetPiece.Penalty) {
    return {
      backX: clamp(x + (taking ? 50 : 11) * side, -PITCH.halfLength, PITCH.halfLength),
      frontX: clamp(x - (taking ? 10 : 20) * side, -PITCH.halfLength, PITCH.halfLength),
      lowY: -PITCH.halfWidth * 0.8,
      highY: PITCH.halfWidth * 0.8,
      xFocus: x,
      xFocusStrength: taking ? 0.6 : 1,
      yFocus: 0,
      yFocusStrength: taking ? 0.8 : 1,
    };
  }

  // Throw-in. All three lengthways numbers are measured off the ball rather than off the pitch,
  // so they run off the end of it at a throw-in taken deep: 30 m behind a ball on the 45 m mark
  // is 75 m, well past the 55 m goal line. The original clamps each one to the pitch as it is
  // computed (`teamAIcontroller.cpp:774-776,781-783`), and that clamp is the only thing keeping
  // the eleven on the field — the formation mapping it feeds never clamps its own answer
  // (`AIfunctions.cpp:72-180`). Without it the deeper half of the side defending that end is
  // mapped off the pitch and then pinned flat on its own goal line.
  return {
    backX: clamp(x + 30 * side, -PITCH.halfLength, PITCH.halfLength),
    frontX: clamp(x - (taking ? 20 : 15) * side, -PITCH.halfLength, PITCH.halfLength),
    lowY: -PITCH.halfWidth * 0.75 + y * 0.25,
    highY: PITCH.halfWidth * 0.75 + y * 0.25,
    xFocus: clamp(x + (taking ? -4 : 16) * side, -PITCH.halfLength, PITCH.halfLength),
    xFocusStrength: taking ? 0.4 : 0.2,
    yFocus: y * (taking ? 0.996 : 0.95),
    yFocusStrength: taking ? 0.6 : 0.5,
    microFocus: setup.position,
    microFocusStrength: 0.7,
  };
}

function mapFormation(entry: FormationEntry, side: -1 | 1, p: Placement): Vector3 {
  const position = entry.position.clone();
  const midfieldStrength = p.midfieldFocusStrength ?? 0;
  if (midfieldStrength > 0) {
    const stretch = curve(clamp(1 - Math.abs(position.x * 1.2), 0, 1), 1) * midfieldStrength;
    position.x =
      position.x * (1 - stretch) + ((p.midfieldFocus ?? 0.5) * 2 - 1) * stretch;
  }

  position.x = p.backX + (position.x * 0.5 + 0.5) * (p.frontX - p.backX);
  position.y = p.lowY + (position.y * -side * 0.5 + 0.5) * (p.highY - p.lowY);
  const pure = position.clone();

  if ((p.xFocusStrength ?? 0) > 0) {
    const length = Math.max(Math.abs(p.backX - p.frontX), 0.001);
    let bias = 1 - clamp(Math.abs((p.xFocus ?? 0) - position.x) / length, 0, 1);
    bias = Math.pow(-Math.cos(bias * Math.PI) * 0.5 + 0.5, 0.8) * p.xFocusStrength!;
    position.x = position.x * (1 - bias) + p.xFocus! * bias;
  }

  if ((p.yFocusStrength ?? 0) > 0) {
    const width = Math.max(Math.abs(p.highY - p.lowY), 0.001);
    const distance = clamp(Math.abs((p.yFocus ?? 0) - position.y) / width, 0, 1);
    const bias =
      (1 - distance) *
      (0.2 + (0.8 * Math.abs(p.yFocus ?? 0)) / PITCH.halfWidth) *
      p.yFocusStrength!;
    position.y = position.y * (1 - bias) + p.yFocus! * bias;
  }

  if (p.microFocus && (p.microFocusStrength ?? 0) > 0) {
    const distance = p.microFocus.clone().sub(pure).multiply(new Vector3(1, 0.8, 0)).length() / 50;
    if (distance < 1) {
      let bias = curve(1 - distance, 0.3);
      bias += (1 - normalizedClamp(Math.abs(distance - 0.15), 0, 0.25)) * 0.1;
      bias = clamp(bias, 0, 1) * p.microFocusStrength!;
      const focus = new Vector3(
        p.microFocus.x,
        p.microFocus.y * 0.6 + position.y * 0.4,
        0,
      );
      position.multiplyScalar(1 - bias).addScaledVector(focus, bias);
    }
  }

  position.x = clamp(position.x, -PITCH.halfLength, PITCH.halfLength);
  position.y = clamp(position.y, -PITCH.halfWidth, PITCH.halfWidth);
  return position;
}
